import { secondsToMilliseconds } from "~/tools/date-time";
import type { Object } from "~/tools/object";
import { type RequestStatus, idleStatus, isLoadingRequestStatus, loadingStatus } from "~/tools/request-status";
import { type Observable, exhaustMap, filter, startWith, switchMap, takeUntil, timer } from "~/tools/rxjs";

export const DEFAULT_POLL_INTERVAL = secondsToMilliseconds(10);

export interface PollRequestProps<E, T, P extends Object, M = unknown> {
	startPoll: Observable<P>;
	stopPoll: Observable<void>;
	interval?: number;
	// Side effects
	request: (params: P) => Observable<RequestStatus<E, T, M>>;
	ignoreIntermediaryLoadings?: boolean;
}

/**
 * @deprecated avoid using anything related to rxjs
 */
export const pollRequest = <E, T, P extends Object, M = unknown>({
	startPoll,
	stopPoll,
	ignoreIntermediaryLoadings = true,
	interval = DEFAULT_POLL_INTERVAL,
	request,
}: PollRequestProps<E, T, P, M>): Observable<RequestStatus<E, T, M>> => {
	return startPoll.pipe(
		/** calling startPoll again cancels old timer */
		switchMap((params) =>
			timer(0, interval).pipe(
				/** if http request is pending – no need to trigger timer again */
				exhaustMap(() =>
					/** Don't care about loading requests in the middle of polling */
					request(params).pipe(filter((value) => (ignoreIntermediaryLoadings ? !isLoadingRequestStatus(value) : true))),
				),
				startWith(loadingStatus<M>()),
				/** Stop only the current polling timer.
				 * Please note that new startPoll event will create new timer with new polling */
				takeUntil(stopPoll),
			),
		),
		/** On the very start polling start with idle status */
		startWith(idleStatus<M>()),
	);
};
